import { getColors } from "@/shared/utils/ganttColors";
import { ShiftReportProductionSummary } from "@/pages/shift-report/models/ShiftReportResponse";
import { Box } from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import "moment-duration-format";

const formatWithCampaign = (
	value: number | null,
	campaignValue: number | null,
	decimals = 0,
): string => {
	if (value == null) return "";
	const formatted = decimals > 0 ? value.toFixed(decimals) : String(value);
	if (campaignValue != null) {
		const campaignFormatted =
			decimals > 0 ? campaignValue.toFixed(decimals) : String(campaignValue);
		if (campaignFormatted !== formatted) {
			return `${formatted} (${campaignFormatted})`;
		}
	}
	return formatted;
};

export const SectionCellRenderer = ({
	value,
	color,
}: {
	value: string;
	color: string;
}) => {
	return (
		<Box display="flex" alignItems="center" gap={1}>
			<Box
				sx={{
					width: 10,
					height: 10,
					borderRadius: "50%",
					backgroundColor: color,
					flexShrink: 0,
				}}
			/>
			{value}
		</Box>
	);
};

export const useTableData = (hiddenColumns?: string[]) => {
	const allColumnDefs: ColDef[] = [
		{
			field: "SectionName",
			headerName: "Section",
			minWidth: 20,
			flex: 1,
			cellRenderer: SectionCellRenderer,
			cellRendererParams: (params: ICellRendererParams) => {
				const data = params.data as ShiftReportProductionSummary;
				return {
					value: data.SectionName,
					color: getColors(data.SectionId!).fill,
				};
			},
			cellClass: "heat-cell",
			cellStyle: { justifyContent: "flex-start" },
		},
		{
			field: "HeatsCount",
			headerName: "Heats",
			minWidth: 20,
			flex: 1,
			cellStyle: { justifyContent: "center" },
		},
		{
			field: "ChargedColdTons",
			headerName: "Tons Charged",
			minWidth: 70,
			flex: 1,
			valueFormatter: (params) => {
				const data = params.data as ShiftReportProductionSummary;
				return formatWithCampaign(
					data?.ChargedColdTons,
					data?.CampaignChargedColdTons,
					2,
				);
			},
			cellStyle: { justifyContent: "center" },
		},
		{
			field: "ChargedBillets",
			headerName: "Billet Quantity",
			minWidth: 70,
			flex: 1,
			valueFormatter: (params) => {
				const data = params.data as ShiftReportProductionSummary;
				return formatWithCampaign(
					data?.ChargedBillets,
					data?.CampaignChargedBillets,
				);
			},
			cellStyle: { justifyContent: "center" },
		},
		{
			field: "RolledColdTons",
			headerName: "Tons Rolled",
			minWidth: 70,
			flex: 1,
			valueFormatter: (params) => {
				const data = params.data as ShiftReportProductionSummary;
				return formatWithCampaign(
					data?.RolledColdTons,
					data?.CampaignRolledColdTons,
					2,
				);
			},
			cellStyle: { justifyContent: "center" },
		},
		{
			field: "RolledBillets",
			headerName: "Billet Rolled",
			minWidth: 70,
			flex: 1,
			valueFormatter: (params) => {
				const data = params.data as ShiftReportProductionSummary;
				return formatWithCampaign(
					data?.RolledBillets,
					data?.CampaignRolledBillets,
				);
			},
			cellStyle: { justifyContent: "center" },
		},
		{
			field: "AvgTonsPerHour",
			headerName: "TPH",
			minWidth: 20,
			flex: 1,
			valueFormatter: (params) =>
				params.value != null ? params.value.toFixed(2) : "",
			cellStyle: { justifyContent: "center" },
		},
		{
			field: "Duration",
			headerName: "Hours Rolled",
			minWidth: 70,
			flex: 1,
			valueFormatter: (params) => {
				const data = params.data as ShiftReportProductionSummary;
				const clipped = data?.Duration;
				const full = data?.CampaignDuration;
				return formatWithCampaign(
					clipped != null ? clipped / 3600 : null,
					full != null ? full / 3600 : null,
					2,
				);
			},
			cellStyle: { justifyContent: "center" },
		},
		{
			field: "RejectedBillets",
			headerName: "Rejects",
			minWidth: 60,
			flex: 1,
			cellStyle: { justifyContent: "center" },
		},
		{
			field: "Cobbles",
			headerName: "Cobbles",
			minWidth: 60,
			flex: 1,
			valueFormatter: (params) => {
				const data = params.data as ShiftReportProductionSummary;
				return formatWithCampaign(data?.Cobbles, data?.CampaignCobbles);
			},
			cellStyle: { justifyContent: "center" },
		},
		{
			headerName: "Charged Yield Loss",
			minWidth: 90,
			flex: 1,
			valueGetter: (params) => {
				const data = params.data as ShiftReportProductionSummary;
				if (data?.ChargedColdTons && data?.RolledColdTons) {
					return (
						((data.ChargedColdTons - data.RolledColdTons) /
							data.ChargedColdTons) *
						100
					);
				}
				return null;
			},
			valueFormatter: (params) =>
				params.value != null ? `${parseFloat(params.value.toFixed(2))}%` : "",
			cellStyle: { justifyContent: "center" },
		},
		{
			headerName: "Rolled Yield Loss",
			minWidth: 90,
			flex: 1,
			valueGetter: (params) => {
				const data = params.data as ShiftReportProductionSummary;
				if (data?.RolledColdTons && data?.RejectedColdTons) {
					return (data.RejectedColdTons / data.RolledColdTons) * 100;
				}
				return null;
			},
			valueFormatter: (params) =>
				params.value != null ? `${parseFloat(params.value.toFixed(2))}%` : "",
			cellStyle: { justifyContent: "center" },
		},
	];

	const columnDefs = hiddenColumns?.length
		? allColumnDefs.filter(
				(col) => !hiddenColumns.includes(col.headerName ?? ""),
			)
		: allColumnDefs;

	return { columnDefs };
};
